import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Users, Home, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function FamiliesPage() {
  const { api } = useAuth();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    family_name: '',
    father_name: '',
    father_phone: '',
    father_email: '',
    mother_name: '',
    mother_phone: '',
    mother_email: '',
    address: '',
    child_lives_with: 'birth_parents',
    notes: ''
  });

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const response = await api.get('/families');
      setFamilies(response.data);
    } catch (error) {
      console.error('Failed to fetch families:', error);
      toast.error('Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/families', formData);
      toast.success('Family created successfully');
      setDialogOpen(false);
      fetchFamilies();
      setFormData({
        family_name: '',
        father_name: '',
        father_phone: '',
        father_email: '',
        mother_name: '',
        mother_phone: '',
        mother_email: '',
        address: '',
        child_lives_with: 'birth_parents',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create family');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Families</h1>
          <p className="text-slate-500 mt-1">Manage family and household records</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="add-family-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Family
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">Register New Family</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="family_name">Family Name *</Label>
                <Input
                  id="family_name"
                  value={formData.family_name}
                  onChange={(e) => setFormData({ ...formData, family_name: e.target.value })}
                  placeholder="e.g., Smith Family"
                  required
                  data-testid="family-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700">Father/Guardian 1</h4>
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.father_name}
                      onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.father_phone}
                      onChange={(e) => setFormData({ ...formData, father_phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.father_email}
                      onChange={(e) => setFormData({ ...formData, father_email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700">Mother/Guardian 2</h4>
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.mother_name}
                      onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.mother_phone}
                      onChange={(e) => setFormData({ ...formData, mother_phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.mother_email}
                      onChange={(e) => setFormData({ ...formData, mother_email: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Child Lives With</Label>
                <Select
                  value={formData.child_lives_with}
                  onValueChange={(value) => setFormData({ ...formData, child_lives_with: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="birth_parents">Birth Parents</SelectItem>
                    <SelectItem value="foster_parents">Foster Parents</SelectItem>
                    <SelectItem value="one_parent">One Parent</SelectItem>
                    <SelectItem value="adoptive_parents">Adoptive Parents</SelectItem>
                    <SelectItem value="step_parent">Parent and Step-Parent</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90" data-testid="family-submit-btn">
                  Create Family
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Families Table */}
      <Card className="card-icrcl">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : families.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Home className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No families registered yet</p>
              <Button
                variant="link"
                className="text-[#2a9d8f]"
                onClick={() => setDialogOpen(true)}
              >
                Add your first family
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Family</TableHead>
                  <TableHead>Parents/Guardians</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Children</TableHead>
                  <TableHead>Living Arrangement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {families.map((family) => (
                  <TableRow key={family.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#f472b6]/10 flex items-center justify-center">
                          <Home className="w-5 h-5 text-pink-500" />
                        </div>
                        <p className="font-medium text-slate-900">{family.family_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {family.father_name && <p className="text-sm">{family.father_name}</p>}
                        {family.mother_name && <p className="text-sm">{family.mother_name}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {family.father_phone && (
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {family.father_phone}
                          </p>
                        )}
                        {family.father_email && (
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {family.father_email}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {family.patient_ids?.length || 0} children
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600 capitalize">
                        {family.child_lives_with?.replace(/_/g, ' ') || '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
